import { useNavigate, useSearchParams } from 'react-router';
import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { AuthHeading } from '@/bundle/Auth/ui/AuthHeading/AuthHeading';
import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { SinglePaymentSummaryDetails } from '@/bundle/_MasterAdmin/_SinglePaymentCheckoutPage/ui/SinglePaymentSummaryDetails';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getBuyOverageWiresPath } from '@/bundle/_MasterAdmin/_BuyOverageWiresPage/path/path';
import { Notification } from '@/components/Notification/Notification';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { MAX_OVERAGE_WIRE_COUNT, MIN_OVERAGE_WIRE_COUNT } from '@/bundle/_MasterAdmin/_BuyOverageWiresPage/const/const';
import { getBillingPlanDetails } from '@/bundle/shared/api';
import { SinglePaymentCheckoutWidget } from '@/bundle/shared/components/Checkout/SinglePayment/SinglePaymentCheckoutWidget';
import { useUser } from '@/context/userContext';
import { getBillingDetails } from '@/helpers/billingHelpers';
import { getMasterAdminBillingPath } from '../_BillingPage/path/path';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { SinglePaymentSuccessSnackbarMessage } from './const/const';
import { SubscriptionCheckoutWrapper } from './styles';

export const SinglePaymentCheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const wiresQuantityParam = searchParams.get('wires_quantity');
  const wiresQuantity = Number(wiresQuantityParam);
  const isValidWiresQuantity = wiresQuantity >= MIN_OVERAGE_WIRE_COUNT && wiresQuantity <= MAX_OVERAGE_WIRE_COUNT;
  const planId = getBillingDetails(user)?.current_plan?.id;

  const { data } = useQuery({
    queryKey: ['get_billing_plan_details', planId, wiresQuantity],
    queryFn: () => getBillingPlanDetails(planId),
    enabled: !!planId && isValidWiresQuantity,
  });

  useEffect(() => {
    if (!planId || !isValidWiresQuantity) {
      navigate(getBuyOverageWiresPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planDetails = data?.body;
  const error = getResponseError(data?.error);

  const navigateToBilling = () => {
    navigate(getMasterAdminBillingPath());
    showSnackbar(<SinglePaymentSuccessSnackbarMessage />, { maxWidth: '400px', autoClose: false });
  };

  return (
    <AuthLayout position='top'>
      <AuthHeading
        header='Complete Your Purchase'
        subHeader='Please review purchase details below, then continue to secure payment.'
      />
      {error && (
        <>
          <Notification variant='error' marginBottom='16px'>
            {error}
          </Notification>
          <NavigationLink to={getBuyOverageWiresPath()}>{'< Return to Buy Overage Wires'}</NavigationLink>
        </>
      )}
      {planDetails && (
        <SubscriptionCheckoutWrapper>
          <AuthStep>
            <SinglePaymentCheckoutWidget wiresQuantity={wiresQuantity} onSuccess={navigateToBilling} />
          </AuthStep>
          <SinglePaymentSummaryDetails plan={planDetails} wiresQuantity={wiresQuantity} />
        </SubscriptionCheckoutWrapper>
      )}
    </AuthLayout>
  );
};
